import {computed, effect, inject, Injectable, InjectionToken, Injector, signal, Signal, untracked} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup} from '@angular/forms';
import {nanoid} from 'nanoid';
import {
    combineLatest,
    defer,
    firstValueFrom,
    from,
    merge,
    MonoTypeOperatorFunction,
    Observable,
    of,
    shareReplay
} from 'rxjs';
import {combineLatestWith, distinctUntilChanged, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {TypedForm, TypedFormControls} from '../../../common/form-types';
import {getFormControlValueWithUpdatesStream} from '../../../common/form-utils';
import {Side} from '../../../enums/side';
import {AppConfigService} from '../../../services/config.service';
import {DexLpTradeFeeService} from '../../../widgets/dex-lp-trade-widget/dex-lp-trade-fee.service';
import {
    DexErc20InsufficientAllowanceError
} from '../../../widgets/dex-lp-trade-widget/trade-execution/dex-known-errors';
import {DexTradeService} from '../../../widgets/dex-lp-trade-widget/trade-execution/dex-trade.service';
import {DexTxError} from '../../../widgets/dex-lp-trade-widget/trade-execution/dex-tx-error';
import {getViemRevertFallbackMessage} from '../../../widgets/dex-lp-trade-widget/trade-execution/revert-error';
import {AutocompleteConfig} from '../../autocomplete';
import {
    DeliverableDepositFilter
} from '../../deliverable-input/deliverable-autocomplete/filters/deliverable-deposit-filter';
import {destroy$} from '../../destroyable';
import {DynWallet} from '../../dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../../dynamic-xyz/dynamic-zyx-daemon.service';
import {LoggerService} from '../../logger/logger.service';
import {DEX_RATE_REF_SYMBOL} from '../dex-streaming/const';
import {DexSecuritiesSubjectService} from '../dex-streaming/subjects/dex-securities-subject-service';
import {ComputedTranche} from '../dex-streaming/types/computed/computed-tranche';
import {Deliverable, isDeliverablesEqual} from '../dex-streaming/types/deliverable';
import {numToValueWithDel, ValueWithDel, ValueWithDelAndComments} from '../dex-streaming/utils';
import {DexTrancheUtilityService} from '../dex-tranche-utility/dex-tranche-utility.service';
import {RefBalance, TrancheDelRefBalanceService} from '../dex-tranche-utility/tranche-del-ref-balance.service';

export interface BuyFormValue {
    asset: Deliverable;
    qty: number;
    rejectIfExceeds: any;
}

export interface SellFormValue {
    qty: any;
    rejectIfExceeds: any;
}

export interface DepositTxDeps {
    tr: ComputedTranche;
    d: Deliverable;
    dQty: bigint;
    wallet: DynWallet;
}

export interface WithdrawTxDeps {
    tr: ComputedTranche;
    dQty: bigint;
    wallet: DynWallet;
}

export interface EstGas {
    vd?: ValueWithDel | undefined;
    isLoading?: boolean;
}

export interface ErrorInfo {
    dexTxError?: DexTxError;
    allowanceError?: DexErc20InsufficientAllowanceError;
    fallbackErrorMsg?: string;
}

/** Prefill for the forms, resolved by the host (page/widget) */
export interface DexLpTradeInitialValues {
    assetSymbol?: string;
    /** decimal string, settings-style */
    qty?: string;
}

/** Everything the controller needs from the host; no widget-system types inside */
export interface DexLpTradeContext {
    /** Tranche to trade; the host owns its resolution */
    tranche: Signal<ComputedTranche | undefined>;
    side: Side;
    initial?: DexLpTradeInitialValues;
    /** Late (post-handoff) tx error handler; desktop passes its reveal-service handler */
    onLateError?: (error: unknown) => void;
    isLateErrorDelegatedToNotification?: boolean;
}

export const DEX_LP_TRADE_CONTEXT = new InjectionToken<DexLpTradeContext>('DEX_LP_TRADE_CONTEXT');

const inc = (c: number) => c + 1;

const getLogMath = (isEnabled: boolean): MonoTypeOperatorFunction<{ mathComments?: string } & any> =>
    isEnabled
        ? tap(({mathComments}) => console.log([`[${new Date().toISOString()}]`, mathComments].join('\n')))
        : tap(() => undefined);

/**
 * Headless trade logic for tranches: forms, fee/gas/allowance estimation,
 * errors, transaction submission. Platform-agnostic - presentation lives
 * in the hosts (desktop widget, mobile sheet). Knows nothing about the
 * widget system: the tranche, prefill and late-error handling arrive via
 * DEX_LP_TRADE_CONTEXT.
 */
@Injectable()
export class DexLpTradeController {

    /** id to link label and input in templates */
    public readonly id = nanoid();

    // --- forms and streams (assigned in the constructor) ---
    public buyForm: TypedForm<BuyFormValue>;
    public sellForm: TypedForm<SellFormValue>;

    public refBalance$: Observable<RefBalance>;
    public trancheRefBalance$: Observable<RefBalance>;
    public trancheRateEst$: Observable<RefBalance>;
    public fee$: Observable<ValueWithDelAndComments | undefined>;

    /**
     * Buy-asset autocomplete config; re-derived when the tranche changes
     * (same cadence as the desktop form rebuild used to give).
     */
    public readonly $buyDelAutocompleteConfig = computed<AutocompleteConfig>(() => {
        const tr = this.$tranche();

        return {
            type: {
                provider: undefined,
                filters:  [this.deliverableDepositFilter.config({trancheId: tr?.raw.poolTrancheId})]
            }
        };
    });

    // --- UI state ---
    public readonly $isSending = signal<boolean>(false);
    public readonly $isRequestingAllowance = signal<boolean>(false);
    public readonly estGasSignal = signal<EstGas | undefined>(undefined);
    public readonly refQtySignal = signal<Observable<RefBalance> | undefined>(undefined);
    public readonly $error = signal<ErrorInfo | undefined>(undefined);

    public readonly $depositBlackoutStr = computed<string>(() => {
        const rules = this.$tranche()?.$trancheRules();
        return this.formatTimeFromSecStr(rules?.raw.withdrawalRules?.postDepositBlackoutPeriod);
    });

    public readonly $withdrawBlackoutStr = computed<string>(() => {
        const rules = this.$tranche()?.$trancheRules();
        return this.formatTimeFromSecStr(rules?.raw.withdrawalRules?.postWithdrawalBlackoutPeriod);
    });

    private readonly injector = inject(Injector);

    private readonly deliverableDepositFilter = inject(DeliverableDepositFilter);
    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly dexTrancheUtilityService = inject(DexTrancheUtilityService);
    private readonly trancheDelRefBalanceService = inject(TrancheDelRefBalanceService);
    private readonly dynamicZyxDaemonService = inject(DynamicZyxDaemonService);
    private readonly dexTradeService = inject(DexTradeService);
    private readonly dexLpTradeFeeService = inject(DexLpTradeFeeService);
    private readonly loggerService = inject(LoggerService);
    private readonly logMath = getLogMath(inject(AppConfigService).isDevOrCiEnv());
    private readonly ctx = inject(DEX_LP_TRADE_CONTEXT);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly $tranche = this.ctx.tranche;

    /** BUY/SELL; the host switches it via setSide() */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly $side = signal<Side>(this.ctx.side);
    public readonly isBuy = computed(() => this.$side() === Side.BUY);
    public readonly isSell = computed(() => this.$side() === Side.SELL);


    private readonly wallet$ = this.dynamicZyxDaemonService.dynRemote.primaryWallet$;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly $wallet = toSignal(this.wallet$);

    private readonly destroy$ = destroy$();

    private readonly tranche$ = toObservable(this.$tranche, {injector: this.injector}).pipe(
        shareReplay({bufferSize: 1, refCount: true})
    );

    private readonly refQtyTrigger = signal(0);

    private readonly allowanceUpdateSignal = signal(0);
    private readonly triggerGasUpdateSignal = signal(0);
    private readonly triggerDebouncedGasUpdateSignal = signal(0);
    private debouncedGasUpdateTimer: any;

    /** Off by default (desktop parity): gas is estimated via the Estimate button */
    private readonly isAutoGasEstEnabledSignal = signal<boolean>(false);

    private readonly $buyAsset = signal<Deliverable | null>(null, {equal: isDeliverablesEqual});
    private readonly $qty = signal<number | null>(null);

    private readonly depositParamsSignal = computed(() => {
        if (this.$side() !== Side.BUY) return null;

        const tr = this.$tranche();
        const wallet = this.$wallet();
        const asset = this.$buyAsset();
        const qty = this.$qty();

        if (!tr || !wallet || !asset || qty == null) return null;

        return {
            tr,
            d:    asset,
            dQty: numToValueWithDel(qty, asset).v,
            wallet
        } as DepositTxDeps;
    });

    private readonly withdrawParamsSignal = computed(() => {
        if (this.$side() !== Side.SELL) return null;

        const tr = this.$tranche();
        const wallet = this.$wallet();
        const qty = this.$qty();

        if (!tr || !wallet || qty == null) return null;

        return {
            tr,
            dQty: numToValueWithDel(qty, tr.unitDeliverable).v,
            wallet
        } as WithdrawTxDeps;
    });

    private readonly allowanceParamsSignal = computed(() =>
        this.depositParamsSignal() || this.withdrawParamsSignal()
    );

    // --- effects (migrated 1:1) ---
    private readonly estGasDebounceEffect = effect((onCleanup) => {

        // Skip initial call
        if (!this.triggerDebouncedGasUpdateSignal()) {
            return;
        }

        const params = untracked(() =>
            this.depositParamsSignal()
            || this.withdrawParamsSignal()
        );

        if (!params) {
            this.estGasSignal.set(undefined);
            return;
        }

        untracked(() => this.setGasUpdatingState());

        this.debouncedGasUpdateTimer = setTimeout(() => this.triggerGasUpdate(), 1000);
        onCleanup(() => clearTimeout(this.debouncedGasUpdateTimer));
    });

    private readonly autoEstGasEffect = effect(() => {
        const isAutoGasEstEnabled = this.isAutoGasEstEnabledSignal();
        this.depositParamsSignal();
        this.withdrawParamsSignal();

        if (isAutoGasEstEnabled) {
            this.triggerDebouncedGasUpdateSignal.update(inc);
        } else {
            clearTimeout(this.debouncedGasUpdateTimer);
            this.estGasSignal.set(undefined);
        }
    });

    private readonly allowanceEstGasEffect = effect(() => {

        // Skip initial call
        if (!this.allowanceUpdateSignal()) {
            return;
        }

        this.triggerGasUpdate();
    });

    private readonly estimateGasEffect = effect(() => {
        const triggerValue = this.triggerGasUpdateSignal();

        // Skip initial call
        if (!triggerValue) {
            return;
        }

        const params = untracked((): DepositTxDeps | WithdrawTxDeps =>
            this.depositParamsSignal()
            || this.withdrawParamsSignal()
        );

        if (!params) return;

        untracked(() => this.estimateGasAsync(params)).catch();
    });

    private readonly allowanceEffect = effect(() => {
        const isRequesting = this.$isRequestingAllowance();
        if (!isRequesting) return;

        const params = this.allowanceParamsSignal();
        if (!params) return;

        untracked(() => this.processAllowanceRequest(params)).catch();
    });

    private readonly refQtyParamsComputed = computed(() => {
        // side-aware: on SELL the "As USD" value derives from the tranche units
        const asset = this.$side() === Side.BUY
            ? this.$buyAsset()
            : this.$tranche()?.unitDeliverable;
        const qty = this.$qty();
        if (!asset || qty == null) return undefined;

        const vQty = numToValueWithDel(qty, asset);
        const refDel = this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);

        return {vQty, refDel};
    });

    private readonly refQtyEffect = effect(() => {
        const trigger = this.refQtyTrigger();
        if (!trigger) return;

        const params = this.refQtyParamsComputed();
        if (!params) return;

        const refQty$ = this.trancheDelRefBalanceService.getRefCrossBalance(params.vQty, params.refDel);
        this.refQtySignal.set(refQty$);
    });

    constructor() {
        this.initForms();
        this.initObservables();
        this.linkFormSignals();

        // kick of the "As USD" estimation (desktop did it at the end of buildForm)
        this.refQtyTrigger.update(inc);
    }

    // ---------- public API ----------

    /**
     * Switches BUY/SELL, keeping the shared qty consistent with the target
     * form (desktop kept it through the widget settings).
     */
    public setSide(side: Side): void {
        if (this.$side() === side) return;

        this.$side.set(side);

        const qty = this.$qty();
        const targetForm = side === Side.BUY ? this.buyForm : this.sellForm;
        if (targetForm && targetForm.controls.qty.value !== qty) {
            targetForm.controls.qty.setValue(qty);
        }
    }

    /** Enables debounced automatic gas re-estimation (off - desktop parity) */
    public setAutoGasEstimation(enabled: boolean): void {
        this.isAutoGasEstEnabledSignal.set(enabled);
    }

    /**
     * Submits the trade (deposit/withdrawal). The host asks for confirmation
     * BEFORE calling this (desktop: modal; mobile: in-sheet confirm step).
     *
     * @returns ErrorInfo on failure (also set to $error); undefined on success
     * and when a send is already in progress (guard with $isSending in UI).
     */
    public async execute(): Promise<ErrorInfo | undefined> {
        if (this.$isSending()) {
            return undefined;
        }

        this.setError(undefined);
        this.$isSending.set(true);
        this.setFormsEnabled(false);

        try {
            const req$ = this.$side() === Side.BUY
                ? this.sendDeposit()
                : this.sendWithdrawal();

            await firstValueFrom(req$);

            return undefined; // success -> the host closes the widget/sheet
        } catch (e) {
            this.setError(e, `Trade "${this.$side()}"`);
            return this.$error();
        } finally {
            this.setFormsEnabled(true);
            this.$isSending.set(false);
        }
    }

    public onAddAllowance(event?: MouseEvent): void {
        event?.stopPropagation();
        event?.preventDefault();

        this.$isRequestingAllowance.set(true);
    }

    public onDismissError(event?: MouseEvent): void {
        event?.stopPropagation();
        event?.preventDefault();

        this.setError(undefined);
    }

    public onEstimateGasClick(event?: MouseEvent): void {
        event?.stopPropagation();
        event?.preventDefault();

        this.triggerGasUpdate();
    }

    public isGasNotEstimated(): boolean {
        const g = this.estGasSignal();

        return g?.vd?.v === undefined;
    }

    /** Desktop host uses it to replay widget initialError */
    public setError(e: unknown | undefined, tip?: string): void {
        if (!e) {
            this.$error.set(undefined);
        } else {
            const isDexTxError = e instanceof DexTxError;
            const dexTxError: DexTxError = isDexTxError ? e : undefined;
            const errorInfo: ErrorInfo = {
                dexTxError,
                allowanceError:   dexTxError instanceof DexErc20InsufficientAllowanceError ? dexTxError : undefined,
                fallbackErrorMsg: !dexTxError && getViemRevertFallbackMessage(e) || undefined
            };

            this.$error.set(errorInfo);

            if (this.loggerService.isLoggerApiEnabled) {
                const isUnknownError = !dexTxError ||
                    !errorInfo.allowanceError
                    && !dexTxError.naming
                    && !dexTxError.details?.name;

                if (isUnknownError && this.loggerService) {
                    this.loggerService.warn({
                        name:    'Unknown error in Dex Lp Trade',
                        details: {
                            tip,
                            error: e
                        }
                    });
                }
            }
        }
    }

    // ---------- init ----------

    private initForms(): void {
        // Both forms exist at once: the mobile sheet switches sides without
        // rebuilding (desktop rebuilt one form per tranche/settings change).
        const buyControls: TypedFormControls<BuyFormValue> = {
            asset:           new FormControl<Deliverable>(
                this.ctx.initial?.assetSymbol
                    ? this.dexSecuritiesSubjectService.getDeliverableBySymbol(this.ctx.initial.assetSymbol)
                    : null
            ),
            qty:             new FormControl<number>(this.parseInitialQty()),
            rejectIfExceeds: new FormControl<boolean>(false)
        };

        const sellControls: TypedFormControls<SellFormValue> = {
            qty:             new FormControl<number>(this.parseInitialQty()),
            rejectIfExceeds: new FormControl<boolean>(false)
        };

        this.buyForm = new FormGroup(buyControls);
        this.sellForm = new FormGroup(sellControls);
    }

    private parseInitialQty(): number | null {
        const qtyStr = this.ctx.initial?.qty;
        return qtyStr ? parseFloat(qtyStr) : null;
    }

    private initObservables(): void {
        const depositFee$ = this.createDepositFeeStream();
        const withdrawalFee$ = this.createWithdrawalFeeStream();

        // side-reactive fee: both forms exist, so the stream follows $side
        this.fee$ = toObservable(this.$side, {injector: this.injector}).pipe(
            switchMap(side => side === Side.BUY ? depositFee$ : withdrawalFee$),
            shareReplay({bufferSize: 1, refCount: true})
        );

        this.refBalance$ = this.createRefBalanceStream();
        this.trancheRefBalance$ = this.createTrancheRefBalanceStream();
        this.trancheRateEst$ = this.createTrancheRateEstStream();
    }

    /**
     * Form -> signals. Desktop routed this through the widget settings;
     * the controller links the controls to the params/gas/fee signals directly.
     */
    private linkFormSignals(): void {
        getFormControlValueWithUpdatesStream(this.buyForm.controls.asset).pipe(
            takeUntil(this.destroy$)
        ).subscribe(d => this.$buyAsset.set(d ?? null));

        merge(
            getFormControlValueWithUpdatesStream(this.buyForm.controls.qty),
            getFormControlValueWithUpdatesStream(this.sellForm.controls.qty)
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe(q => this.$qty.set(q ?? null));
    }

    private setFormsEnabled(enabled: boolean): void {
        for (const form of [this.buyForm, this.sellForm]) {
            if (!form) continue;
            if (enabled) {
                form.enable();
            } else {
                form.disable();
            }
        }
    }

    // ---------- streams (migrated 1:1 unless commented) ----------

    private createRefBalanceStream(): Observable<RefBalance> {
        return defer(() => {
            const asset$ = getFormControlValueWithUpdatesStream(this.buyForm.controls.asset);

            return this.wallet$.pipe(
                combineLatestWith(asset$),
                switchMap(([wallet, d]) =>
                    this.trancheDelRefBalanceService.getRefBalanceStream(d, wallet.address)
                )
            );
        });
    }

    private createTrancheRefBalanceStream(): Observable<RefBalance> {
        return defer(() =>
            this.wallet$.pipe(
                combineLatestWith(this.tranche$),
                switchMap(([wallet, tr]) =>
                    this.trancheDelRefBalanceService.getRefBalanceStream(tr.unitDeliverable, wallet.address)
                )
            )
        );
    }

    private createTrancheRateEstStream(): Observable<RefBalance> {
        // side-aware asset: BUY - the pay asset, SELL - the tranche units
        // (desktop never built this stream for SELL-initialized widgets)
        const asset$ = toObservable(
            computed(() => this.$side() === Side.BUY
                ? this.$buyAsset()
                : this.$tranche()?.unitDeliverable ?? null),
            {injector: this.injector}
        );
        const numQty$ = toObservable(this.$qty, {injector: this.injector});

        const qty$ = asset$.pipe(
            combineLatestWith(numQty$),
            map(([d, numQty]) => numToValueWithDel(numQty, d)),
            shareReplay({bufferSize: 1, refCount: true})
        );

        return combineLatest({tr: this.tranche$, qty: qty$, fee: this.fee$}).pipe(
            switchMap(({tr, qty, fee}) =>
                this.trancheDelRefBalanceService.getRefCrossBalanceExcludingFee(qty, tr.unitDeliverable, fee)
            ),
            this.logMath,
            shareReplay({bufferSize: 1, refCount: true})
        );
    }

    private createDepositFeeStream(): Observable<ValueWithDelAndComments | undefined> {
        return defer(() => {
            const asset$ = toObservable(this.$buyAsset, {injector: this.injector});
            const numQty$ = toObservable(this.$qty, {injector: this.injector});

            return this.tranche$.pipe(
                combineLatestWith(asset$, numQty$),
                switchMap(([tr, d, numQty]) => {
                    if (!tr || !d || !numQty) {
                        return of(undefined);
                    }

                    const vQty = numToValueWithDel(numQty, d);

                    return from(
                        this.dexLpTradeFeeService.getDepositFee(tr.raw.poolTrancheId, d.deliverableId, vQty)
                    ).pipe(
                        this.logMath
                    );
                })
            );
        });
    }

    private createWithdrawalFeeStream(): Observable<ValueWithDelAndComments | undefined> {
        return defer(() => {
            const numQty$ = toObservable(this.$qty, {injector: this.injector});

            return this.tranche$.pipe(
                combineLatestWith(numQty$),
                switchMap(([tr, numQty]) => {
                    if (!tr || !numQty) {
                        return of(undefined);
                    }

                    const vQty = numToValueWithDel(numQty, tr.unitDeliverable);

                    return from(
                        this.dexLpTradeFeeService.getWithdrawalFee(tr, vQty)
                    );
                })
            );
        });
    }

    // ---------- submission (migrated 1:1; onLateError from the context) ----------

    private sendDeposit(): Observable<unknown> {
        return defer(() =>
            this.getDepositTxDepsStream().pipe(
                take(1),
                switchMap(({tr, d, dQty, wallet}) =>
                    from(this.dexTradeService.deposit({
                        tr, d, dQty, wallet,
                        onLateError: e => this.onLateError(e),
                        isLateErrorDelegatedToNotification: this.ctx.isLateErrorDelegatedToNotification
                    }))
                )
            )
        );
    }

    private sendWithdrawal(): Observable<unknown> {
        return defer(() =>
            this.getWithdrawTxDepsStream().pipe(
                take(1),
                switchMap(({tr, dQty, wallet}) =>
                    from(this.dexTradeService.withdraw({
                        tr, dQty, wallet,
                        onLateError: e => this.onLateError(e),
                        isLateErrorDelegatedToNotification: this.ctx.isLateErrorDelegatedToNotification
                    }))
                )
            )
        );
    }

    private getDepositTxDepsStream(): Observable<DepositTxDeps> {
        return defer(() => {
            const tr$ = this.tranche$.pipe(
                distinctUntilChanged((t1, t2) => t1?.raw.poolTrancheId === t2?.raw.poolTrancheId)
            );
            const asset$ = toObservable(this.$buyAsset, {injector: this.injector});
            const numQty$ = toObservable(this.$qty, {injector: this.injector});
            const wallet$ = this.dynamicZyxDaemonService.dynRemote.primaryWallet$.pipe(
                distinctUntilChanged((w1, w2) => w1?.address === w2?.address)
            );

            return tr$.pipe(
                combineLatestWith(asset$, numQty$, wallet$),
                map(([tr, d, numQty, wallet]) => ({
                    tr,
                    d,
                    dQty: numToValueWithDel(numQty, d).v,
                    wallet
                }))
            );
        });
    }

    private getWithdrawTxDepsStream(): Observable<WithdrawTxDeps> {
        return defer(() => {
            const tr$ = this.tranche$.pipe(
                distinctUntilChanged((t1, t2) => t1?.raw.poolTrancheId === t2?.raw.poolTrancheId)
            );
            const numQty$ = getFormControlValueWithUpdatesStream(this.sellForm.controls.qty).pipe(
                distinctUntilChanged()
            );
            const wallet$ = this.dynamicZyxDaemonService.dynRemote.primaryWallet$.pipe(
                distinctUntilChanged((w1, w2) => w1?.address === w2?.address)
            );

            return tr$.pipe(
                combineLatestWith(numQty$, wallet$),
                map(([tr, numQty, wallet]) => ({
                    tr,
                    dQty: numToValueWithDel(numQty, tr.unitDeliverable).v,
                    wallet
                }))
            );
        });
    }

    // ---------- allowance / gas (migrated 1:1) ----------

    private async processAllowanceRequest(params: DepositTxDeps | WithdrawTxDeps): Promise<void> {
        this.$isRequestingAllowance.set(true);

        const d: Deliverable = this.$side() === Side.BUY
            ? (params as DepositTxDeps).d
            : params.tr.unitDeliverable;

        try {
            await this.dexTradeService.queuedRequestAllowance({...params, d});
            this.allowanceUpdateSignal.update(inc);

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            // Silently handle errors
        } finally {
            this.$isRequestingAllowance.set(false);
        }
    }

    private async estimateGasAsync(params: DepositTxDeps | WithdrawTxDeps): Promise<void> {
        this.setGasUpdatingState();

        try {
            let gas: bigint;

            if (this.$side() === Side.BUY) {
                gas = await this.dexTradeService.estimateDepositGas(params as DepositTxDeps);
            } else {
                gas = await this.dexTradeService.estimateWithdrawGas(params as WithdrawTxDeps);
            }

            const vd = this.dexTrancheUtilityService.nativeTokenToVd(gas);
            this.estGasSignal.set({vd, isLoading: false});
        } catch (e) {
            if (!(e instanceof DexTxError)) {
                console.warn('Estimate Gas Error', e);
            }

            this.setError(e, `Estimate Gas for "${this.$side()}"`);
            this.estGasSignal.set({isLoading: false});
        }
    }

    private setGasUpdatingState(): void {
        if (this.estGasSignal()?.isLoading !== true) {
            this.estGasSignal.update(v => ({...v, isLoading: true}));
        }

        this.setError(undefined);
    }

    private triggerGasUpdate(): void {
        clearTimeout(this.debouncedGasUpdateTimer);
        this.triggerGasUpdateSignal.update(inc);
    }

    private formatTimeFromSecStr(secStr: string | undefined): string {
        return secStr ? String(Number(secStr) / 60) : '';
    }

    /** Late tx errors: host handler (desktop reveal) or logging fallback */
    private onLateError(e: unknown): void {
        if (this.ctx.onLateError) {
            this.ctx.onLateError(e);
        } else if (this.loggerService.isLoggerApiEnabled) {
            this.loggerService.warn({
                name:    'Late error in Dex Lp Trade',
                details: {error: e}
            });
        }
    }

}
